import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Zap, AlertCircle, Settings2 } from "lucide-react";
import { getCurrency } from '../dca/ExchangeUIConfigs';
import { NotificationSettings } from '../dca/settings/NotificationSettings';

// 분할한 하위 컴포넌트 import
import { SignalBasicSettings } from './settings/SignalBasicSettings';
import { SignalStrategySettings } from './settings/SignalStrategySettings';
import { SignalTpSlSettings } from './settings/SignalTpSlSettings';
import { SignalSidePanel } from './settings/SignalSidePanel';

interface SignalBotSettingsProps {
  botData: any;
  setBotData: (data: any) => void;
  enabledExchanges: Record<string, boolean>;
  onSave: () => void;
  onCancel: () => void;
}

export const SignalBotSettings: React.FC<SignalBotSettingsProps> = ({
  botData,
  setBotData,
  enabledExchanges,
  onSave,
  onCancel
}) => {
  const { settings, is_active, symbol, entry_type, principal_amount, entry_amount, leverage, strategy_type } = botData;

  // Ensure Uptrend Guard defaults exist so checkbox can toggle even when bot is running
  React.useEffect(() => {
    const defaults = {
      use_uptrend_daily_guard: false,
      use_uptrend_4h_guard: false,
      uptrend_daily_gain_pct: 5.0,
      uptrend_gain_pct_4h: 2.5,
      uptrend_atr_multiple_4h: 1.5,
    };
    const merged = { ...defaults, ...(botData.settings || {}) };
    // Only update if missing keys
    if (JSON.stringify(merged) !== JSON.stringify(botData.settings)) {
      setBotData((prev: any) => ({ ...prev, settings: merged }));
    }
  }, []);
  const isRunning = is_active;
  const [applyToSameStrategy, setApplyToSameStrategy] = useState(false);
  const [applyToFuture, setApplyToFuture] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);
  const currency = getCurrency(symbol);

  const updateBotData = (key: string, value: any) => {
    setBotData((prev: any) => ({ ...prev, [key]: value }));
  };

  const updateSettings = (key: string, value: any) => {
    setBotData((prev: any) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  const updateMultiSettings = (updates: Record<string, any>) => {
    setBotData((prev: any) => ({
      ...prev,
      settings: { ...prev.settings, ...updates }
    }));
  };

  const updateMultiple = (updates: Record<string, any>) => {
    setBotData((prev: any) => ({ ...prev, ...updates }));
  };

  const [activeInfoTab, setActiveInfoTab] = React.useState<'backtest' | 'portfolio' | 'guide'>('backtest');
  const [backtestPeriod, setBacktestPeriod] = React.useState<'15m' | '30m' | '1h' | '4h'>('1h');

  // 증거금 및 Notional 진입규모 계산
  const effectiveMargin = entry_type === 'percent'
    ? (principal_amount * (entry_amount / 100))
    : entry_amount;
  const positionSize = (effectiveMargin || 0) * (leverage || 1);

  return (
    <div className="max-w-6xl mx-auto py-4 animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col lg:flex-row gap-6 items-start">
      <Card className="flex-1 bg-card shadow-sm border-border rounded-2xl overflow-hidden w-full">
        <CardHeader className="bg-muted/30 border-b border-border p-6 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-border/50 pb-4">
            <div className="space-y-1">
              <h2 className="text-lg font-black flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary fill-primary/20" />
                시그널 봇 설정
              </h2>
              <p className="text-xs text-muted-foreground font-bold px-1">추세 및 지표 기반 자동 매매 설정</p>
            </div>
            <div className="flex gap-2">
              {strategy_type === 'channel_trend' && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isRunning}
                  onClick={() => {
                    setBotData((prev: any) => ({
                      ...prev,
                      strategy_type: 'channel_trend',
                      interval: '5m',
                      leverage: 10,
                      entry_type: 'percent',
                      entry_amount: 10,
                      settings: {
                        ...prev.settings,
                        boundary_mode: 'edge',
                        lr_period: 50,
                        boundary_pct: 20,
                        tp_pct: 1.5,
                        sl_pct: 1.0,
                        min_profit_pct: 0.5,
                        use_midline_tp: true,
                        use_rsi_filter: true,
                        rsi_oversold: 35,
                        rsi_overbought: 65,
                        use_ema_filter: true,
                        ema_period: 200,
                        use_trend_filter: true,
                        use_trailing_stop: true,
                        trailing_activation_pct: 0.7,
                        trailing_callback_pct: 0.2,
                        use_channel_breakout_sl: true,
                        channel_breakout_sl_pct: 1.0,
                        use_uptrend_daily_guard: true,
                        uptrend_daily_gain_pct: 5.0,
                        use_uptrend_4h_guard: true,
                        uptrend_gain_pct_4h: 2.5,
                        uptrend_atr_multiple_4h: 1.5
                      }
                    }));
                    alert('channel_trend 추천 전략 프리셋이 적용되었습니다!');
                  }}
                  className="h-8 text-[10px] font-black border-primary/30 text-primary hover:bg-primary/10 rounded-lg gap-1.5"
                >
                  <Zap className="w-3 h-3 fill-primary" />
                  channel_trend 추천 셋팅
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onCancel} className="text-muted-foreground font-bold h-8 rounded-lg">취소</Button>
            </div>
          </div>

          {/* 1. 기본 설정 컴포넌트 */}
          <SignalBasicSettings
            botData={botData}
            updateBotData={updateBotData}
            updateSettings={updateSettings}
            updateMultiple={updateMultiple}
            isRunning={isRunning}
            enabledExchanges={enabledExchanges}
            currency={currency}
            effectiveMargin={effectiveMargin}
            positionSize={positionSize}
          />
        </CardHeader>

        <CardContent className="p-8 space-y-8">
          {/* 텔레그램 알림 설정 */}
          <div className="border-b border-border/50 pb-8">
            <NotificationSettings
              telegramEnabled={botData.telegram_enabled ?? null}
              setTelegramEnabled={(val) => updateBotData('telegram_enabled', val)}
              telegramNotifyTypes={botData.telegram_notify_types || ['entry', 'tp', 'sl', 'liquidate', 'error', 'info']}
              setTelegramNotifyTypes={(val) => updateBotData('telegram_notify_types', val)}
              isLive={botData.is_live}
              isReadOnly={isRunning}
            />
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-2 px-1">
              <Settings2 className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-black">전략 세부 설정</h3>
            </div>

            {/* 2. 전략 세부 설정 컴포넌트 */}
            <SignalStrategySettings
              botData={botData}
              updateSettings={updateSettings}
              updateMultiSettings={updateMultiSettings}
            />

            {/* 3. 익절/손절 TP/SL 세부 설정 컴포넌트 */}
            <SignalTpSlSettings
              botData={botData}
              updateSettings={updateSettings}
            />
          </div>

          {botData.source === 'auto_scanner' && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-4 text-xs leading-relaxed space-y-1.5">
              <div className="font-black text-purple-600 text-sm mb-1">🤖 Auto Scanner 봇 설정 안내</div>
              <div className="text-muted-foreground font-medium">
                <span className="text-foreground font-bold">Scanner 재활성화 시 적용:</span> 전략 타입 · SL/TP · Boundary · LR Period · Filter 등
              </div>
              <div className="text-muted-foreground font-medium">
                <span className="text-foreground font-bold">사용자 설정 유지:</span> 거래소 · 거래모드(Live/Testnet/Sim) · 레버리지 · 진입금액 · 매매 모드
              </div>
            </div>
          )}

        </CardContent>

        <CardFooter className="bg-muted/30 p-6 flex flex-col gap-4 border-t border-border">
          {isRunning && (
            <div className="w-full flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <p className="text-[11px] text-amber-600 font-bold">
                봇이 현재 가동 중입니다. 핵심 설정을 변경하려면 먼저 봇을 정지해야 합니다.
              </p>
            </div>
          )}

          {botData.id && (
            <div className={`w-full space-y-3 p-4 rounded-xl border ${isRunning ? 'bg-muted/30 border-border/30 opacity-60 pointer-events-auto' : 'bg-amber-500/5 border-amber-500/20'}`}>
              <h4 className="text-[11px] font-black flex items-center gap-1.5 text-amber-600">
                <Settings2 className="w-3.5 h-3.5" /> 일괄 적용 옵션
                {isRunning && <span className="text-[9px] font-normal text-muted-foreground ml-1">(봇 정지 후 설정 저장 시 적용 가능)</span>}
              </h4>
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="apply-same-strategy"
                  checked={applyToSameStrategy}
                  disabled={isRunning}
                  onCheckedChange={(c) => setApplyToSameStrategy(!!c)}
                />
                <Label htmlFor="apply-same-strategy" className={`text-[10px] font-bold leading-relaxed ${isRunning ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                  같은 전략(<span className="text-primary">{strategy_type}</span>)의 모든 기존 봇에도 동일하게 적용
                </Label>
              </div>
              {botData.source === 'auto_scanner' && (
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="apply-future"
                    checked={applyToFuture}
                    disabled={isRunning}
                    onCheckedChange={(c) => setApplyToFuture(!!c)}
                  />
                  <Label htmlFor="apply-future" className={`text-[10px] font-bold leading-relaxed ${isRunning ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
                    앞으로 생성될 자율 스캐너 봇에도 자동 적용 (글로벌 기본값 저장)
                  </Label>
                </div>
              )}
              {isRunning && (
                <p className="text-[9px] text-muted-foreground italic">봇을 정지한 후 저장하면 일괄 적용이 가능합니다.</p>
              )}
            </div>
          )}

          <div className="flex w-full gap-4">
            <Button onClick={onCancel} variant="outline" className="flex-1 py-6 rounded-xl font-bold bg-background">취소</Button>
            <Button
              onClick={async () => {
                setBatchSaving(true);
                const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

                // 1. 현재 봇 저장
                try {
                  const res = await fetch(`/signal/${botData.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                    body: JSON.stringify({ bot: botData })
                  });
                  const data = await res.json();
                  if (data.bot && typeof setBotData === 'function') {
                    setBotData(data.bot);
                  }
                } catch (e) {
                  console.error('Save failed:', e);
                }

                // 2. 글로벌 기본값 저장 (앞으로 생성될 봇)
                if (applyToFuture && botData.source === 'auto_scanner') {
                  const globalDefaults: Record<string, any> = {
                    sl_pct: botData.settings?.sl_pct,
                    tp_pct: botData.settings?.tp_pct,
                    leverage: botData.leverage,
                    entry_amount: botData.entry_amount,
                    entry_type: botData.entry_type,
                    boundary_pct: botData.settings?.boundary_pct,
                    lr_period: botData.settings?.lr_period,
                    use_rsi_filter: botData.settings?.use_rsi_filter,
                    use_ema_filter: botData.settings?.use_ema_filter,
                    use_adx_filter: botData.settings?.use_adx_filter,
                    adx_threshold: botData.settings?.adx_threshold,
                    adx_mode: botData.settings?.adx_mode,
                    use_midline_tp: botData.settings?.use_midline_tp,
                    use_trailing_stop: botData.settings?.use_trailing_stop,
                    trailing_activation_pct: botData.settings?.trailing_activation_pct,
                    trailing_callback_pct: botData.settings?.trailing_callback_pct,
                    use_channel_breakout_sl: botData.settings?.use_channel_breakout_sl,
                    channel_breakout_sl_pct: botData.settings?.channel_breakout_sl_pct,
                    use_limit_order: botData.settings?.use_limit_order,
                    filter_type: botData.settings?.filter_type,
                    trigger_mode: botData.settings?.trigger_mode,
                    symbol_tp_sl: botData.settings?.symbol_tp_sl,
                  };
                  try {
                    await fetch('/signal/save_future_defaults', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                      body: JSON.stringify({ settings: globalDefaults })
                    });
                  } catch (e) {
                    console.error('Failed to save future defaults:', e);
                  }
                }

                // 3. 같은 전략 기존 봇 일괄 적용
                if (applyToSameStrategy) {
                  const settingsToApply: Record<string, any> = { ...botData.settings };
                  ['side', 'entry_price', 'stop_loss_price', 'take_profit_targets', 'risk', 'reason', 'strategy_label', 'interval'].forEach(k => delete settingsToApply[k]);
                  settingsToApply.leverage = botData.leverage;
                  settingsToApply.entry_amount = botData.entry_amount;
                  try {
                    await fetch('/signal/batch_update', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                      body: JSON.stringify({
                        filters: { strategy_type: [botData.strategy_type], source: 'auto_scanner' },
                        settings: settingsToApply
                      })
                    });
                  } catch (e) {
                    console.error('Failed to batch update:', e);
                  }
                }

                setBatchSaving(false);
                onSave(); // 폼 닫기
              }}
              disabled={batchSaving}
              className="flex-1 py-6 rounded-xl bg-primary text-primary-foreground font-black shadow-lg"
            >
              {batchSaving ? '일괄 저장 중...' : (botData.id ? '설정 저장하기' : '봇 생성하기')}
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* 4. 우측 전략 설명 & 백테스트 결과 패널 */}
      <SignalSidePanel
        activeInfoTab={activeInfoTab}
        setActiveInfoTab={setActiveInfoTab}
        backtestPeriod={backtestPeriod}
        setBacktestPeriod={setBacktestPeriod}
        strategy_type={strategy_type}
        symbol={symbol}
      />
    </div>
  );
};
